import { useState } from "react";
import { genreOptions, regionOptions, yearOptions } from "@/mocks/movies";
import type { MovieFilter } from "@/utils/movie";

interface FilterPanelProps {
  filter: MovieFilter;
  onChange: (key: keyof MovieFilter, value: string) => void;
  onClear: () => void;
  hasActive: boolean;
}

const typeOptions = ["全部", "电影", "电视剧"];

function OptionRow({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: string[];
  value: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="py-4 border-b border-background-200/70 last:border-b-0">
      <h4 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
        <span className="w-1 h-4 rounded-full bg-accent-500" />
        {label}
      </h4>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onSelect(opt)}
              className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                active
                  ? "bg-primary-500 text-background-50 font-semibold"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterPanel({ filter, onChange, onClear, hasActive }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <aside className="w-full lg:w-64 lg:shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="lg:hidden w-full flex items-center justify-between px-4 py-3 mb-3 rounded-lg bg-background-50 border border-background-200 cursor-pointer"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
          <i className="ri-filter-3-line text-primary-600" />
          筛选条件
          {hasActive && <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px]">已选</span>}
        </span>
        <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-xl text-foreground-600`} />
      </button>

      <div
        className={`lg:sticky lg:top-24 rounded-lg bg-background-50 border border-background-200 p-4 ${
          open ? "block" : "hidden lg:block"
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <i className="ri-equalizer-line text-primary-600" />
            多维筛选
          </span>
          {hasActive && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap cursor-pointer"
            >
              清除筛选
            </button>
          )}
        </div>

        <OptionRow
          label="类型"
          options={typeOptions}
          value={filter.type || "全部"}
          onSelect={(v) => onChange("type", v)}
        />
        <OptionRow
          label="地区"
          options={regionOptions}
          value={filter.region || "全部"}
          onSelect={(v) => onChange("region", v)}
        />
        <OptionRow
          label="年份"
          options={yearOptions}
          value={filter.year || "全部"}
          onSelect={(v) => onChange("year", v)}
        />
        <OptionRow
          label="题材"
          options={genreOptions.slice(0, 11)}
          value={filter.genre || "全部"}
          onSelect={(v) => onChange("genre", v)}
        />
      </div>
    </aside>
  );
}